import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { extractErrorList, getFormData } from 'src/app/shared/helpers/utils';
import { Add_Debtor_Creditor_FormProvider } from '../common/providers/add-debtor-creditor-form-provider';
import { ORIGINATOR_ID } from 'src/app/shared/enums/common.enum';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CCY_SORT_ORDER, TYPES } from 'src/app/shared/constants/helpers.const';
import { COUNTRY_LIST } from 'src/app/shared/constants/countryList.const';
import { HttpClient } from '@angular/common/http';
import { HttpService } from 'src/app/services/http.service';
import { TooltipData_Debtor_Creditor } from '../common/constants/tooltip.data';
import Swal from 'sweetalert2';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { DebtorCreditorHttpService } from '../common/services/debtor-creditor-http.service';
import { ProfileDetails } from 'src/app/shared/interfaces/profileDetails-response.interface';
import { TradeHttpService } from 'src/app/transactions/services/trade-http.service';
import { Store } from '@ngrx/store';
import { selectCurrencyList } from '../../shared/state/selectors/currency.selector';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { MasterDataHttpService } from '../../shared/services/master-data-http.service';

@Component({
  selector: 'app-add-debtor-creditor',
  templateUrl: './add-debtor-creditor.component.html',
  styleUrls: ['./add-debtor-creditor.component.scss'],
})
export class AddDebtorCreditorComponent implements OnInit, OnDestroy {
  @ViewChild('fileUploader') fileUploader!: ElementRef;
  private subscriptions: Subscription = new Subscription();
  addForm: FormGroup;
  tooltipData = TooltipData_Debtor_Creditor;
  uploadedFiles: any;
  isSubmitted = false;
  isSameAsPrimaryAddress = false;
  years: { id: string; name: string }[] = [];
  types: { id: string; name: string }[] = TYPES;
  countryList: { id: string; name: string; countryCode: string }[] =
    COUNTRY_LIST.map((item) => ({
      id: item.name,
      name: item.name,
      countryCode: item.countryCode,
    }));
  currencyList: { id: string; name: string }[] = [];
  allBankData: { id: string; name: string }[] = [];
  originatorId = ORIGINATOR_ID;
  userDetails: ProfileDetails;
  showUploadLoader = false;
  isSelected = false;
  ccySortOrder = CCY_SORT_ORDER;
  toggleSettings = {
    labels: {
      unchecked: 'Person',
      checked: 'Organisation',
    },
    values: {
      unchecked: 'person',
      checked: 'organisation',
    },
    switchColor: {
      checked: '#fff',
      unchecked: '#fff',
    },
    color: {
      unchecked: '#d4d5db',
      checked: '#d4d5db',
    },
    fontColor: {
      checked: 'black',
      unchecked: 'black',
    },
  };
  isPersonEntity: boolean;
  isShowPreviousInput: boolean;
  classificationList: { id: number; value: string }[] = [];

  @Input() type!: string;

  constructor(
    private formProvider: Add_Debtor_Creditor_FormProvider,
    public activeModal: NgbActiveModal,
    private apiClient: HttpClient,
    private http: HttpService,
    private debtorCreditorHttpService: DebtorCreditorHttpService,
    private tradeTransactionsHttpService: TradeHttpService,
    private store: Store,
    private masterDataHttpService: MasterDataHttpService,
  ) {}

  get experienceFileCtrl(): FormControl {
    return this.addForm.get('experienceFile') as FormControl;
  }

  ngOnInit(): void {
    this.addForm = this.formProvider.initForm();
    if (this.type) {
      this.addForm.get('type')?.setValue(this.type);
    }
    this.getYears();
    this.loadDropDownsData();
    this.getCcyList();
    this.subscribeToTypeChange();
    this.subscribeToSortCodeValueChange();
    this.subscribeToPrimaryAddressChanges();
    this.getUserProfileData();
    this.getClassificationData();
  }

  getUserProfileData() {
    this.userDetails = JSON.parse(localStorage.getItem('userData') || 'null');
  }

  showPhoneAsterisk(group: 'mobile' | 'home' | 'office'): boolean {
    const phones = this.addForm?.get('phoneNumbers');
    const mobile = phones?.get('mobile.number')?.value;
    const home = phones?.get('home.number')?.value;
    const office = phones?.get('office.number')?.value;
    const anyFilled = !!(mobile || home || office);
    // Before any phone is filled → show * on all three
    // After at least one is filled → show * only on the filled ones
    return !anyFilled || !!phones?.get(`${group}.number`)?.value;
  }

  getAddressGroup(index: number): FormGroup {
    return (this.addForm.get('addresses') as FormArray).at(index) as FormGroup;
  }

  getAddressControl(index: number, controlName: string): AbstractControl {
    return this.getAddressGroup(index).get(controlName)!;
  }

  handleValidatePhone(group: 'mobile' | 'home' | 'office'): void {
    const phoneGroup = this.addForm.get('phoneNumbers')?.get(group);
    if (!phoneGroup) return;

    const countryCode = phoneGroup.get('countryCode')?.value;
    const number = phoneGroup.get('number')?.value;
    const extension = phoneGroup.get('extension')?.value;

    if (!countryCode && !number && !extension) {
      phoneGroup.get('countryCode')!.setErrors(null);
      return;
    }

    const currentPhone = `${countryCode ?? ''}${number ?? ''}${extension ?? ''}`;
    const isValidNumber = parsePhoneNumberFromString(
      currentPhone,
    )?.isValid() as boolean;

    if (currentPhone && !isValidNumber) {
      phoneGroup
        .get('countryCode')!
        .setErrors({ invalidPhoneNumber: true }, { emitEvent: true });
    } else {
      phoneGroup.get('countryCode')!.setErrors(null, { emitEvent: true });
    }
    this.addForm.updateValueAndValidity();
  }

  onSubmit() {
    this.isSubmitted = true;
    this.addForm.markAllAsTouched();
    if (this.addForm.invalid) return;
    let rawData = this.addForm.getRawValue();

    rawData.sortCode = rawData.sortCode?.toString().replace(/-/g, '');

    const phones = rawData.phoneNumbers ?? {};
    rawData.home = [
      phones.home?.countryCode,
      phones.home?.number,
      phones.home?.extension,
    ]
      .filter(Boolean)
      .join('');
    rawData.office = [
      phones.office?.countryCode,
      phones.office?.number,
      phones.office?.extension,
    ]
      .filter(Boolean)
      .join('');
    rawData.mobile = [
      phones.mobile?.countryCode,
      phones.mobile?.number,
      phones.mobile?.extension,
    ]
      .filter(Boolean)
      .join('');
    delete rawData.phoneNumbers;

    rawData = getFormData(rawData);
    this.doCreate(rawData);
  }

  doCreate(data: any): void {
    data.roleName = data.type;
    data.type = this.isPersonEntity ? 2 : 1;

    const reqData: any = {
      ...(!this.isPersonEntity && { name: data.organisationName }),
      // name: this.isPersonEntity ? `${data.firstName} ${data.lastName}` : data.organisationName,
      type: data.type,
      roleName: data.roleName,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      tradeName: data.tradeName,
      registrationNumber: data.registrationNumber,
      birthDate: data.birthDate,
      previousName: data.previousName,
      loyalSince: data.loyalSince,
      totalHistoricValue: data.totalHistoricValue,
      paymentCycle: data.paymentCycle?.toLocaleString(),
      creditNotes: data.creditNotes,
      invoicesPredicted: data.invoicesPredicted,
      predictedRevenue: data.predictedRevenue,
      classificationId: data.classificationId,
      experienceFile: data.experienceFile,
    };
    const primaryAddr = (data.addresses ?? [])[0] ?? {};
    const billingAddrRaw = (data.addresses ?? [])[1] ?? {};
    const billingAddr = this.isSameAsPrimaryAddress
      ? primaryAddr
      : billingAddrRaw;

    // Filter out empty address objects
    reqData.addresses = [
      {
        title: 'Primary',
        country: primaryAddr.country || '',
        countryCode: primaryAddr.countryCode || '',
        state: primaryAddr.state || '',
        city: primaryAddr.city || '',
        area: primaryAddr.area || '',
        lineOne: primaryAddr.lineOne || '',
        lineTwo: primaryAddr.lineTwo || '',
        zipCode: primaryAddr.zipCode || '',
      },
      {
        title: 'Secondary',
        country: billingAddr.country || '',
        countryCode: billingAddr.countryCode || '',
        state: billingAddr.state || '',
        city: billingAddr.city || '',
        area: billingAddr.area || '',
        lineOne: billingAddr.lineOne || '',
        lineTwo: billingAddr.lineTwo || '',
        zipCode: billingAddr.zipCode || '',
      },
    ].filter((address) =>
      Object.values(address).some(
        (value) => value !== undefined && value !== null && value !== '',
      ),
    );

    // Remove empty phoneNumbers
    const phoneNumbers = {
      home: data.home,
      office: data.office,
      mobile: data.mobile,
    };
    if (
      Object.values(phoneNumbers).some(
        (value) => value !== undefined && value !== null && value !== '',
      )
    ) {
      reqData.phoneNumbers = phoneNumbers;
    }

    // Remove empty bankAccount
    const bankAccount = {
      roleNames: ['Credebtor'],
      bankEntityId: data.bankId ? data.bankId : null,
      name: data.bankName,
      swiftBic: data.swiftBic,
      sortCode: data.sortCode,
      account: data.account?.toString(),
      iban: data.iban,
      ccy: data.ccy,
    };
    if (
      Object.values(bankAccount).some(
        (value) => value !== undefined && value !== null && value !== '',
      )
    ) {
      reqData.bankAccount = bankAccount;
    }
    this.debtorCreditorHttpService.postCredebtor(reqData).subscribe({
      next: (response: any) => {
        this.activeModal.close(response);
        this.addForm.reset();
        Swal.fire({
          icon: 'success',
          text: 'Credebtor successfully added',
          showConfirmButton: false,
          timer: 5000,
        });
      },
      error: (error: any) => {
        Swal.fire({
          icon: 'error',
          html: 'Error',
          text: 'An error occurred while adding Credebtor',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    });
  }

  private extractError(err: any): string {
    const messages: any = typeof err === 'string' ? err : extractErrorList(err);
    const paragraphs =
      typeof err === 'string'
        ? messages
        : messages.map((item: any) => `<p>${item}</p>`);
    return typeof err === 'string' ? paragraphs : paragraphs.join('\n');
  }

  getYears(): void {
    this.years = []; // Ensure the years array is reset
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= 1950; year--) {
      this.years.push({ id: year.toString(), name: year.toString() });
    }
  }

  subscribeToTypeChange(): void {
    const typeChangeSubscription = this.addForm
      .get('type')
      ?.valueChanges.subscribe((value) => {
        this.addForm.get('bankStatementFile')?.setValue('');
        this.isSelected = false;
        this.showUploadLoader = false;
      });
    if (typeChangeSubscription) {
      this.subscriptions.add(typeChangeSubscription);
    }
  }

  isPrimaryAddress() {
    this.updateBillingAddressValidators(this.isSameAsPrimaryAddress);
  }

  private subscribeToPrimaryAddressChanges(): void {
    const primaryGroup = this.getAddressGroup(0);
    const sub = primaryGroup.valueChanges.subscribe((primaryValues) => {
      if (this.isSameAsPrimaryAddress) {
        const billingGroup = this.getAddressGroup(1);
        billingGroup.patchValue(primaryValues, { emitEvent: false });
      }
    });
    this.subscriptions.add(sub);
  }

  updateBillingAddressValidators(isSame: boolean) {
    const billingGroup = this.getAddressGroup(1);
    const primaryGroup = this.getAddressGroup(0);

    if (isSame) {
      // Copy all fields from primary address into billing
      [
        'lineOne',
        'lineTwo',
        'city',
        'state',
        'area',
        'zipCode',
        'country',
        'countryCode',
      ].forEach((field) => {
        billingGroup
          .get(field)
          ?.setValue(primaryGroup.get(field)?.value ?? null);
      });
      // Disable billing group — Angular form directives will apply disabled state to DOM inputs
      billingGroup.disable();
    } else {
      // Re-enable billing group, keep existing values so user can edit them
      billingGroup.enable();
    }
  }

  closeModal() {
    this.activeModal.dismiss();
  }

  downloadTemplate() {
    const filename = 'Debtor-Experience-Upload.xlsx';
    const apiUrl = 'assets/resources/Debtor-Experience-Upload.xlsx';
    this.apiClient
      .get(apiUrl, { responseType: 'arraybuffer' })
      .subscribe((data: ArrayBuffer) => {
        const blob = new Blob([data], { type: 'text/xlsx' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  fileChange(element: any): void {
    this.uploadedFiles = element.target.files[0];
    const maxSize = 10000000; // 10 MB
    const allowedFileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // XLSX MIME type
    const file = element.target.files[0];
    if (!file) {
      this.showUploadLoader = false;
      this.uploadedFiles = null;
      element.target.value = '';
      return;
    }

    if (file.type !== allowedFileType) {
      this.uploadedFiles = null;
      element.target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: Only PDF files are allowed.',
        showConfirmButton: true,
        timer: 5000,
      });
      return;
    }

    if (file.size > maxSize) {
      this.uploadedFiles = null;
      element.target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: File size must be less than 10 MB.',
        showConfirmButton: true,
        timer: 5000,
      });

      return;
    }

    this.experienceFileCtrl.setValue(this.uploadedFiles);
  }

  clearFileInput(fileUploader: HTMLInputElement): void {
    fileUploader.value = '';
    fileUploader.files = null;
    this.isSelected = false;
    this.addForm.get('experienceFile')?.setValue(null);
    this.addForm.get('experienceFile')?.updateValueAndValidity();
  }

  generateStringFromArrayWithLineBreak(array: string[]): string {
    let result = '';
    array.forEach((item, index) => {
      result += item;
      if (index < array.length - 1) {
        result += '<hr></br>';
      }
    });
    return result;
  }

  loadDropDownsData() {
    this.http.getEntityBanks().subscribe((response: any) => {
      this.allBankData = response.map((bank: any) => ({
        id: bank.bankId,
        name: bank.name,
      }));
    });
  }

  private getCcyList(): void {
    this.store
      .select(selectCurrencyList)
      .subscribe((currencyList: string[]) => {
        this.currencyList = [...currencyList]
          .map((item) => ({ id: item, name: item }))
          .sort(
            (a, b) =>
              this.ccySortOrder.indexOf(a.id) - this.ccySortOrder.indexOf(b.id),
          );
      });
  }

  onBankChange(event: any): void {
    this.addForm.get('bankName')?.setValue(event.name);
  }

  onCountryChange(event: any, addressIndex: number): void {
    this.getAddressGroup(addressIndex)
      .get('countryCode')
      ?.setValue(event.countryCode);
  }

  private subscribeToSortCodeValueChange() {
    const sortCodeControl = this.addForm.get('sortCode');
    if (sortCodeControl) {
      this.subscriptions = sortCodeControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter((data) => !!data),
        )
        .subscribe((value: string) => {
          // eslint-disable-next-line no-useless-escape
          const sanitizedValue = value.replace(/[^0-9\-]/g, '');
          const formattedValue = this.formatSortCode(sanitizedValue);
          sortCodeControl.patchValue(formattedValue, { emitEvent: false });
        });
    }
  }

  private formatSortCode(value: string): string {
    return value.replace(/(\d{2})(?=\d)/g, '$1-');
  }

  preventNegative(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e') {
      event.preventDefault();
    }
  }

  /**
   * . toggleChange - Handles toggle change between person and organisation
   * Handles toggle change between person and organisation
   * Updates isPersonEntity flag based on selected type
   * @param event - Toggle event value ('person' or 'organisation')
   */
  toggleChange(event: any): void {
    this.isPersonEntity = event === 'person';
  }

  /**
   * . handlePreviewCheck - Handles previous entity checkbox change
   * Handles previous entity checkbox change
   * Updates flag to show/hide previous entity input
   * @param event - Checkbox change event
   */
  handlePreviewCheck(event: any) {
    this.isShowPreviousInput = event.target.checked;
  }

  getClassificationData(): void {
    this.masterDataHttpService
      .getClassificationList()
      .subscribe((classifications) => {
        this.classificationList = classifications.map((item) => ({
          id: item.id,
          value: item.value,
        }));
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
